const route = useRoute()

// A function that determines whether the user can access the route
const metaPermission = () => {
    const localePath = useLocalePath()
    const authStore: any = useAuthStore()

    watch(() => route.meta, (newMeta, oldMeta) => {
        const userRole = authStore.user.role;

        // If roles meta is not defined, allow the user to use the current view
        if (!newMeta.permission || newMeta.permission.length === 0) {
            return;
        }

        // If user role is user and don't have permission to report a problem
        // Redirect to dashboard
        if (authStore.user.role == 'user' && !authStore.user.can_report_a_problem && (route.path == '/dashboard/reports' || route.path == '/dashboard/reports/add')) {
            // Redirecting to dashboard
            return navigateTo(localePath('/dashboard'));
        }

        // master-sys-admin is a superset of system-admin — accept it anywhere
        // system-admin is allowed
        const allowed = newMeta.permission.includes(userRole)
            || (userRole === 'master-sys-admin' && newMeta.permission.includes('system-admin'));

        // If user's role is not in meta's roles array, redirect the user to the dashboard
        if (newMeta.permission.length > 0 && !allowed) {
            // Redirecting to dashboard
            return navigateTo(localePath('/dashboard'));
        } else {
            // Permission granted
        }
    }, {
        immediate: true,
    });
}

/* 
    --- Composable for user permissions based on role ---
    
    - Retrieve user roles
    - Determine permissions based on role

    / * TODO: Permissions Check * /
    / * Example of what is needed for checking permission of user instead of role * /
    
    function(permission){
        // We take permission prop for example: sport-parks.create
        // Check if user have this permission and return true or false
        const result = authStore.user.permissions.includes(permission)
        return result
    }
*/

// A function that determines whether the user can access the content
const roleHasPermission = (roles: Array<string> | string) => {
    const authStore: any = useAuthStore()
    const userRole: string = authStore.user.role

    // master-sys-admin is a superset of system-admin
    if (userRole === 'master-sys-admin' && roles.includes('system-admin')) {
        return true;
    }

    return roles.includes(userRole)
}

// Feature access hierarchy (controlled by backend features):
// Basic: Web & Mobile apps, Light controls, Match Mode, Unlimited Users, Roles & Permissions
// Plus: Everything in Basic + Smart Time Scheduling, Event Scheduling, Energy Monitoring, Activity Log
// Premium: Everything in Plus + APIs Integration, Club Knowledge Base, Streetlights Integration

// Helper function to check if user has access to a specific feature from backend
const hasFeatureAccess = (featurePath: string): boolean => {
    const authStore: any = useAuthStore()
    const billingStore: any = useBillingStore()
    const userRole: string = authStore.user.role

    // System admins always have access
    if (userRole == 'system-admin' || userRole == 'master-sys-admin' || userRole == 'mechanic' || userRole == 'installer') {
        return true;
    }

    // For customer-admin and user roles, check feature features_flags from billing store
    if (userRole === 'customer-admin' || userRole === 'user') {
        return billingStore.hasFeature(featurePath);
    }

    return false;
}

// A function that determines whether the user can access Energy Monitoring (Consumption Data)
// This checks the 'energy-consumption.show' feature flag from backend
const hasConsumptionAccess = (): boolean => {
    return hasFeatureAccess('energy-consumption.show');
}

// Check access to Activity Log
// This checks the 'activity-log.show' feature flag from backend
const hasActivityLogAccess = (): boolean => {
    return hasFeatureAccess('activity-log.show');
}

// Check access to Time Scheduling
// This checks the 'event-schedule.show' feature flag from backend
const hasTimeScheduleAccess = (): boolean => {
    return hasFeatureAccess('event-schedule.show');
}

// Check access to Event Scheduling
// This checks the 'events.create', 'events.edit' and 'events.delete' feature flags from backend
const hasEventsAccess = (): boolean => {
    return hasFeatureAccess('events.create') || hasFeatureAccess('events.edit') || hasFeatureAccess('events.delete');
}

// Check access to APIs Integration
// This checks the 'access.control' feature flag from backend
const hasAPIIntegrationAccess = (): boolean => {
    return hasFeatureAccess('access.control');
}

// Check access to External Events
// This checks the 'events.external' feature flag from backend
const hasExternalEventsAccess = (): boolean => {
    return hasFeatureAccess('events.external');
}

// Helper to check if a specific customer object has a feature
// Used when checking features for a customer different from the logged-in user (e.g., event's customer)
const customerHasFeature = (customer: any, featurePath: string): boolean => {
    return customer?.feature_flags?.[featurePath] ?? false;
}

// Helper to check if a specific customer has events access (create, edit, or delete)
const customerHasEventsAccess = (customer: any): boolean => {
    return customerHasFeature(customer, 'events.create') || 
           customerHasFeature(customer, 'events.edit') || 
           customerHasFeature(customer, 'events.delete');
}

export {
    roleHasPermission,
    metaPermission,
    hasConsumptionAccess,
    hasFeatureAccess,
    hasActivityLogAccess,
    hasTimeScheduleAccess,
    hasEventsAccess,
    hasAPIIntegrationAccess,
    hasExternalEventsAccess,
    customerHasFeature,
    customerHasEventsAccess
};
